import { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardList, BookOpen, ListTodo, Loader2, Plus, X, Trash2, Search, Eye, GripVertical } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { EmployeePicker } from "../../components/os/EmployeePicker";
import { api } from "../../lib/api";

const TASK_TONE = { pendiente: "bg-gold/15 text-gold", en_progreso: "bg-sky-500/15 text-sky-600", completada: "bg-emerald-500/15 text-emerald-600" };
const PRIO_TONE = { baja: "text-navy/40", media: "text-sky-600", alta: "text-destructive" };
const FIELD_TYPES = [["text", "Texto"], ["textarea", "Párrafo"], ["number", "Número"], ["select", "Opciones"], ["date", "Fecha"]];

function Modal({ title, onClose, children, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid}>
      <div className={`bg-white rounded-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} p-6 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl">{title}</h3><button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
        {children}
      </div>
    </div>
  );
}

export default function WorkspaceTools() {
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("forms");
  useEffect(() => { api.get("/wtools/meta").then((r) => setMeta(r.data)).catch(() => setMeta({ is_leader: false })); }, []);
  return (
    <OSLayout title="OMNIA Herramientas de Trabajo">
      <div className="space-y-6" data-testid="wtools-page">
        <div className="flex gap-2 border-b border-navy/10 pb-2">
          {[["forms", "Formularios", ClipboardList], ["kb", "Base de Conocimiento", BookOpen], ["tasks", "Tareas", ListTodo]].map(([k, l, Ic]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`wt-tab-${k}`} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${tab === k ? "bg-navy text-cream" : "text-navy/55 hover:bg-navy/5"}`}><Ic className="w-4 h-4" /> {l}</button>
          ))}
        </div>
        {!meta ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <>
            {tab === "forms" && <Forms meta={meta} />}
            {tab === "kb" && <Knowledge meta={meta} />}
            {tab === "tasks" && <Tasks meta={meta} />}
          </>
        )}
      </div>
    </OSLayout>
  );
}

/* ===================== Forms ===================== */
function Forms({ meta }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [respondFor, setRespondFor] = useState(null);
  const [responsesFor, setResponsesFor] = useState(null);
  const load = useCallback(() => { api.get("/wtools/forms").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { try { await api.delete(`/wtools/forms/${id}`); load(); } catch { toast.error("Error"); } };
  return (
    <div className="space-y-3">
      {meta.is_leader && <button onClick={() => setOpen(true)} className="btn btn-primary gap-2" data-testid="wt-form-new"><Plus className="w-4 h-4" /> Nuevo formulario</button>}
      {items.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">Sin formularios.</div>
        : <div className="grid gap-3 sm:grid-cols-2">{items.map((f) => (
          <div key={f.id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`wt-form-${f.id}`}>
            <div className="flex items-start justify-between">
              <div><div className="text-sm font-medium">{f.title}</div>{f.description && <div className="text-xs text-navy/50 mt-0.5">{f.description}</div>}</div>
              {meta.is_leader && <button onClick={() => del(f.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>}
            </div>
            <div className="text-xs text-navy/40 mt-2">{(f.fields || []).length} campos · {f.responses} respuestas</div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setRespondFor(f)} className="btn btn-ghost text-xs" data-testid={`wt-form-respond-${f.id}`}>Responder</button>
              {meta.is_leader && <button onClick={() => setResponsesFor(f)} className="btn btn-ghost text-xs gap-1" data-testid={`wt-form-responses-${f.id}`}><Eye className="w-3.5 h-3.5" /> Respuestas</button>}
            </div>
          </div>
        ))}</div>}
      {open && <FormBuilder onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {respondFor && <RespondForm form={respondFor} onClose={() => setRespondFor(null)} onDone={() => { setRespondFor(null); load(); }} />}
      {responsesFor && <FormResponses form={responsesFor} onClose={() => setResponsesFor(null)} />}
    </div>
  );
}

function FormBuilder({ onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState([{ label: "", type: "text", required: false, options: "" }]);
  const [busy, setBusy] = useState(false);
  const addField = () => setFields([...fields, { label: "", type: "text", required: false, options: "" }]);
  const upd = (i, k, v) => setFields(fields.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)));
  const rm = (i) => setFields(fields.filter((_, idx) => idx !== i));
  const save = async () => {
    if (!title.trim()) return toast.error("Título requerido");
    const clean = fields.filter((f) => f.label.trim()).map((f) => ({ label: f.label.trim(), type: f.type, required: !!f.required, options: f.type === "select" ? f.options.split(",").map((s) => s.trim()).filter(Boolean) : [] }));
    if (!clean.length) return toast.error("Agrega al menos un campo");
    setBusy(true);
    try { await api.post("/wtools/forms", { title, description, fields: clean }); toast.success("Formulario creado"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Nuevo formulario" onClose={onClose} testid="wt-form-dialog" wide>
      <div className="space-y-3">
        <input className="field" placeholder="Título del formulario" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="wt-form-title" />
        <textarea className="field" rows={2} placeholder="Descripción (opcional)" value={description} onChange={(e) => setDescription(e.target.value)} />
        <div className="space-y-2">
          <div className="text-xs font-medium text-navy/50 uppercase tracking-wide">Campos</div>
          {fields.map((f, i) => (
            <div key={i} className="border border-navy/10 rounded-xl p-3 space-y-2" data-testid={`wt-field-${i}`}>
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-navy/20" />
                <input className="field flex-1" placeholder="Etiqueta del campo" value={f.label} onChange={(e) => upd(i, "label", e.target.value)} data-testid={`wt-field-label-${i}`} />
                <select className="field w-32" value={f.type} onChange={(e) => upd(i, "type", e.target.value)} data-testid={`wt-field-type-${i}`}>{FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                {fields.length > 1 && <button onClick={() => rm(i)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
              </div>
              {f.type === "select" && <input className="field" placeholder="Opciones separadas por coma" value={f.options} onChange={(e) => upd(i, "options", e.target.value)} />}
              <label className="flex items-center gap-2 text-xs text-navy/60"><input type="checkbox" checked={f.required} onChange={(e) => upd(i, "required", e.target.checked)} /> Obligatorio</label>
            </div>
          ))}
          <button onClick={addField} className="btn btn-ghost text-xs gap-1" data-testid="wt-field-add"><Plus className="w-3.5 h-3.5" /> Agregar campo</button>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="btn btn-ghost">Cancelar</button><button onClick={save} disabled={busy} className="btn btn-primary" data-testid="wt-form-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear"}</button></div>
    </Modal>
  );
}

function RespondForm({ form, onClose, onDone }) {
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (label, v) => setAnswers({ ...answers, [label]: v });
  const submit = async () => {
    for (const f of form.fields || []) { if (f.required && !String(answers[f.label] || "").trim()) return toast.error(`Falta: ${f.label}`); }
    setBusy(true);
    try { await api.post(`/wtools/forms/${form.id}/respond`, { answers }); toast.success("Respuesta enviada"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Modal title={form.title} onClose={onClose} testid="wt-respond-dialog">
      <div className="space-y-3">
        {(form.fields || []).map((f, i) => (
          <div key={i}>
            <label className="text-xs font-medium text-navy/60 mb-1 block">{f.label}{f.required && <span className="text-destructive"> *</span>}</label>
            {f.type === "textarea" ? <textarea className="field" rows={2} value={answers[f.label] || ""} onChange={(e) => set(f.label, e.target.value)} data-testid={`wt-answer-${i}`} />
              : f.type === "select" ? <select className="field" value={answers[f.label] || ""} onChange={(e) => set(f.label, e.target.value)} data-testid={`wt-answer-${i}`}><option value="">—</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</select>
              : <input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} className="field" value={answers[f.label] || ""} onChange={(e) => set(f.label, e.target.value)} data-testid={`wt-answer-${i}`} />}
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="btn btn-ghost">Cancelar</button><button onClick={submit} disabled={busy} className="btn btn-primary" data-testid="wt-respond-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar"}</button></div>
    </Modal>
  );
}

function FormResponses({ form, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/wtools/forms/${form.id}/responses`).then((r) => setRows(r.data || [])).catch(() => setRows([])); }, [form.id]);
  return (
    <Modal title={`Respuestas · ${form.title}`} onClose={onClose} testid="wt-responses-dialog" wide>
      {!rows ? <div className="py-8 flex justify-center text-navy/40"><Loader2 className="w-5 h-5 animate-spin" /></div>
        : rows.length === 0 ? <div className="text-sm text-navy/40 py-4">Sin respuestas aún.</div>
        : <div className="space-y-3">{rows.map((r) => (
          <div key={r.id} className="border border-navy/10 rounded-xl p-3">
            <div className="text-xs text-navy/40 mb-2">{r.user_name} · {new Date(r.at).toLocaleString("es-MX")}</div>
            <div className="space-y-1">{Object.entries(r.answers || {}).map(([k, v]) => (<div key={k} className="text-sm"><span className="text-navy/50">{k}:</span> <span className="font-medium">{String(v) || "—"}</span></div>))}</div>
          </div>
        ))}</div>}
    </Modal>
  );
}

/* ===================== Knowledge Base ===================== */
function Knowledge({ meta }) {
  const [data, setData] = useState({ articles: [], categories: [] });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [open, setOpen] = useState(false);
  const [reading, setReading] = useState(null);
  const load = useCallback(() => {
    const p = new URLSearchParams(); if (q) p.set("q", q); if (cat) p.set("category", cat);
    api.get(`/wtools/kb?${p.toString()}`).then((r) => setData(r.data || { articles: [], categories: [] })).catch(() => {});
  }, [q, cat]);
  useEffect(() => { load(); }, [load]);
  const openArticle = async (id) => { try { const r = await api.get(`/wtools/kb/${id}`); setReading(r.data); } catch { toast.error("Error"); } };
  const del = async (id) => { try { await api.delete(`/wtools/kb/${id}`); load(); } catch { toast.error("Error"); } };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy/30" /><input className="field pl-9" placeholder="Buscar artículos…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="wt-kb-search" /></div>
        <select className="field w-auto" value={cat} onChange={(e) => setCat(e.target.value)} data-testid="wt-kb-cat"><option value="">Todas las categorías</option>{data.categories.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        {meta.is_leader && <button onClick={() => setOpen(true)} className="btn btn-primary gap-2" data-testid="wt-kb-new"><Plus className="w-4 h-4" /> Nuevo artículo</button>}
      </div>
      {data.articles.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">Sin artículos.</div>
        : <div className="grid gap-3 sm:grid-cols-2">{data.articles.map((a) => (
          <div key={a.id} className="bg-white border border-navy/10 rounded-2xl p-4 cursor-pointer hover:border-violet transition-colors" onClick={() => openArticle(a.id)} data-testid={`wt-kb-${a.id}`}>
            <div className="flex items-start justify-between">
              <div><span className="text-[10px] uppercase tracking-wide bg-violet/10 text-violet px-2 py-0.5 rounded-full">{a.category}</span><div className="text-sm font-medium mt-2">{a.title}</div></div>
              {meta.is_leader && <button onClick={(e) => { e.stopPropagation(); del(a.id); }} className="text-navy/30 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>}
            </div>
            <div className="text-xs text-navy/40 mt-2">{a.author} · {a.views} lecturas</div>
          </div>
        ))}</div>}
      {open && <ArticleEditor onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {reading && <Modal title={reading.title} onClose={() => setReading(null)} testid="wt-kb-read" wide>
        <span className="text-[10px] uppercase tracking-wide bg-violet/10 text-violet px-2 py-0.5 rounded-full">{reading.category}</span>
        <div className="text-xs text-navy/40 mt-2 mb-4">{reading.author} · {reading.views} lecturas</div>
        <div className="text-sm text-navy/80 whitespace-pre-wrap leading-relaxed">{reading.content}</div>
        {(reading.tags || []).length > 0 && <div className="flex flex-wrap gap-1.5 mt-4">{reading.tags.map((t) => <span key={t} className="text-xs bg-navy/5 text-navy/50 px-2 py-0.5 rounded-full">#{t}</span>)}</div>}
      </Modal>}
    </div>
  );
}

function ArticleEditor({ onClose, onSaved }) {
  const [f, setF] = useState({ title: "", category: "General", content: "", tags: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title.trim()) return toast.error("Título requerido");
    setBusy(true);
    try { await api.post("/wtools/kb", { title: f.title, category: f.category || "General", content: f.content, tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean) }); toast.success("Artículo publicado"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Nuevo artículo" onClose={onClose} testid="wt-kb-dialog" wide>
      <div className="space-y-3">
        <input className="field" placeholder="Título" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="wt-kb-title" />
        <input className="field" placeholder="Categoría" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="wt-kb-category" />
        <textarea className="field" rows={8} placeholder="Contenido del artículo…" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="wt-kb-content" />
        <input className="field" placeholder="Etiquetas separadas por coma" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="btn btn-ghost">Cancelar</button><button onClick={save} disabled={busy} className="btn btn-primary" data-testid="wt-kb-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publicar"}</button></div>
    </Modal>
  );
}

/* ===================== Tasks ===================== */
function Tasks({ meta }) {
  const [scope, setScope] = useState("mine");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { api.get(`/wtools/tasks?scope=${scope}`).then((r) => setItems(r.data || [])).catch(() => {}); }, [scope]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => { try { await api.post(`/wtools/tasks/${id}/status`, { status }); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const del = async (id) => { try { await api.delete(`/wtools/tasks/${id}`); load(); } catch { toast.error("Error"); } };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 bg-navy/5 rounded-full p-1">
          {[["mine", "Asignadas a mí"], ["assigned", "Creadas por mí"]].map(([k, l]) => (
            <button key={k} onClick={() => setScope(k)} data-testid={`wt-task-scope-${k}`} className={`px-3 py-1.5 rounded-full text-xs ${scope === k ? "bg-navy text-cream" : "text-navy/55"}`}>{l}</button>
          ))}
        </div>
        <button onClick={() => setOpen(true)} className="btn btn-primary gap-2 ml-auto" data-testid="wt-task-new"><Plus className="w-4 h-4" /> Nueva tarea</button>
      </div>
      {items.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">Sin tareas.</div>
        : items.map((t) => (
          <div key={t.id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`wt-task-${t.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-medium">{t.title} <span className={`text-xs ${PRIO_TONE[t.priority] || ""}`}>· {t.priority}</span></div>
                {t.description && <div className="text-xs text-navy/50 mt-0.5">{t.description}</div>}
                <div className="text-xs text-navy/40 mt-1">Para: {t.assignee_name} · De: {t.creator_name}{t.due_date && ` · Vence: ${t.due_date}`}</div>
              </div>
              <div className="flex items-center gap-2"><span className={`text-xs px-2 py-0.5 rounded-full ${TASK_TONE[t.status]}`}>{t.status.replace("_", " ")}</span><button onClick={() => del(t.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button></div>
            </div>
            <div className="flex gap-2 mt-2">
              {t.status !== "pendiente" && <button onClick={() => setStatus(t.id, "pendiente")} className="btn btn-ghost text-xs">Pendiente</button>}
              {t.status !== "en_progreso" && <button onClick={() => setStatus(t.id, "en_progreso")} className="btn btn-ghost text-xs" data-testid={`wt-task-progress-${t.id}`}>En progreso</button>}
              {t.status !== "completada" && <button onClick={() => setStatus(t.id, "completada")} className="btn btn-primary text-xs" data-testid={`wt-task-done-${t.id}`}>Completar</button>}
            </div>
          </div>
        ))}
      {open && <TaskDialog onClose={() => setOpen(false)} onSaved={() => { setOpen(false); setScope("assigned"); }} />}
    </div>
  );
}

function TaskDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ title: "", description: "", assignee_id: "", due_date: "", priority: "media" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title.trim()) return toast.error("Título requerido");
    if (!f.assignee_id) return toast.error("Selecciona un responsable");
    setBusy(true);
    try { await api.post("/wtools/tasks", f); toast.success("Tarea creada"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Nueva tarea" onClose={onClose} testid="wt-task-dialog">
      <div className="space-y-3">
        <input className="field" placeholder="Título de la tarea" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="wt-task-title" />
        <textarea className="field" rows={2} placeholder="Descripción" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <div><label className="text-xs font-medium text-navy/60 mb-1 block">Responsable</label><EmployeePicker value={f.assignee_id} onChange={(e) => setF({ ...f, assignee_id: e?.id || "" })} testid="wt-task-assignee" /></div>
        <div className="grid grid-cols-2 gap-3">
          <input type="date" className="field" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="wt-task-due" />
          <select className="field" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="wt-task-priority"><option value="baja">Baja</option><option value="media">Media</option><option value="alta">Alta</option></select>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="btn btn-ghost">Cancelar</button><button onClick={save} disabled={busy} className="btn btn-primary" data-testid="wt-task-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear"}</button></div>
    </Modal>
  );
}
